import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  DisplayProps,
  FlexDirectionProps,
  FlexProps,
  HeightProps,
  OverflowProps,
  VerticalAlignProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  display,
  flexDirection,
  height,
  overflow,
  space,
  themeGet,
  verticalAlign,
  width,
} from 'styled-system';

import { useKeyboardSelect } from 'common/hooks/keyboard';
import { useLocalization } from 'common/providers/LocaleProvider';
import { RatingSize } from '../../enums/rating';
import type { FlexShrinkProps, WhiteSpaceProps } from '../../theme/system-utilities';
import { cursor, flexShrink, themeHeight, whiteSpace } from '../../theme/system-utilities';
import Counter from './Counter';
import { useReactRatingProps } from './hooks';
import ReactRating from './ReactRating';
import type { ReactRatingProps } from './ReactRating';

interface RatingProps {
  rate?: number;
  count?: number;
  size?: RatingSize;
  className?: string;
  readonly?: boolean;
  withBackground?: boolean;
  starColor?: string;
  onChange?: (value: number) => void;
  onClick?: () => void;
}

const Container = styled.div<
  FlexProps &
    WidthProps &
    AlignItemsProps &
    DisplayProps &
    FlexDirectionProps &
    WhiteSpaceProps &
    FlexShrinkProps
>`
  ${display};
  ${space};
  ${width};
  ${themeHeight};
  ${flexDirection};
  ${alignItems};
  ${whiteSpace};
  ${flexShrink};
  ${cursor};

  &:focus-visible {
    color: ${themeGet('colors.sys.neutral.text.active')};
    outline-offset: ${themeGet('space.sp_2')}px;
    outline-color: ${themeGet('colors.sys.secondary.border.default')};
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  display: 'inline-flex',
  width: 'fit-content',
  flexDirection: 'row',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flexShrink: 1,
};

const StyledReactRating = styled(ReactRating)<
  ReactRatingProps & HeightProps & OverflowProps & VerticalAlignProps
>`
  ${height};
  ${overflow};

  svg {
    ${verticalAlign};
  }
`;

const styledReactRatingStyles: ComponentProps<typeof StyledReactRating> = {
  overflow: 'hidden',
  verticalAlign: 'top',
};

const Rating = ({
  size = RatingSize.Medium,
  readonly = true,
  rate,
  count,
  onClick,
  className,
  onChange,
  withBackground = false,
  starColor,
}: RatingProps) => {
  const MAX_RATING = 5;
  const { r, t } = useLocalization();

  const reactRatingProps = useReactRatingProps({
    rate,
    size,
    readonly,
    starColor,
    onChange,
  });

  const handleKeyDown = useKeyboardSelect(onClick);

  return (
    <Container
      className={className}
      data-testid="rating"
      role={readonly ? 'group' : 'radiogroup'}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...(!!onClick ? { cursor: 'pointer', tabIndex: 0 } : {})}
      {...(withBackground
        ? {
            themeHeight: ['sp_20', null, 'sp_24', 'sp_32'],
            mt: ['sp_8', null, null, 'sp_12'],
          }
        : {})}
      {...containerStyles}
      aria-label={
        count
          ? `${r.t('h24_seller_rating_aria_label', {
              count,
              total: MAX_RATING,
              rating: rate,
            })} ${t('h24_view_all_reviews_label')}`
          : r.t('h24_rating_stars_label', { rating: rate })
      }
    >
      <StyledReactRating {...styledReactRatingStyles} {...reactRatingProps} />
      <Counter count={count} isSmall={size === RatingSize.Small} isUnderlined={!!onClick} />
    </Container>
  );
};

export default Rating;
